import "./App.css";
import { BrowserRouter as Router, Route, Routes, Link } from "react-router-dom";

import Profile from "./pages/Profile";
import NotFound404 from "./pages/NotFound404";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import ForgotPassword from "./pages/ForgotPassword";
import Home from "./pages/Home";
import Listings from "./pages/Listings";
import Contact from "./pages/Contact";
import CreateListing from "./pages/CreateListing";
import EditListing from "./pages/EditListing";
import ListingDetails from "./pages/ListingDetails";
import PrivateRoute from "./components/PrivateRoute";
import Header from "./components/navbar/Header";
import Footer from "./components/Footer";
import SellerProfile from "./pages/SellerProfile";
import SeeAllFeaturedSellers from "./pages/SeeAllFeaturedSellers";
import SeeAllFeaturedCars from "./pages/SeeAllFeaturedCars";
import SeeAllFeaturedBikes from "./pages/SeeAllFeaturedBikes";
import BuyerRequestsFeed from "./pages/Feed";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import BargainsListings from "./pages/BargainsListings";
import RegisterShowroom from "./pages/RegisterShowroom";
import AddCarToShowroom from "./pages/AddCarToShowroom";
import ShowroomProfile from "./pages/ShowroomProfile";
import { LoadingProvider } from "./contexts/loading/LoadingContext";
import { UserProvider } from "./contexts/user/UserContext";
import { AuthProvider } from "./contexts/AuthContext";
import { Helmet } from "react-helmet";
import ScrollToTop from "./components/scrollToTop/ScrollToTop";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useEffect, useState, useContext } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "./firebase.config";
import { useAuth } from "./contexts/AuthContext";

const ROUTES = {
  Home: { id: "1", element: Home, path: "/" },
  SignIn: { id: "2", element: SignIn, path: "/sign-in" },
  SignUp: { id: "3", element: SignUp, path: "/sign-up" },
  SellerProfile: {
    id: "4",
    element: SellerProfile,
    path: "/seller-profile/:sellerId",
  },
  ForgotPassword: {
    id: "5",
    element: ForgotPassword,
    path: "/forgot-password",
  },
  Listings: { id: "6", element: Listings, path: "/category/:categoryName" },
  CreateListing: { id: "7", element: CreateListing, path: "/create-listing" },
  EditListing: {
    id: "8",
    element: EditListing,
    path: "/edit-listing/:listingId",
  },
  ListingDetails: {
    id: "9",
    element: ListingDetails,
    path: "/category/:categoryName/:listingId",
  },
  SeeAllFeaturedSellers: {
    id: "10",
    element: SeeAllFeaturedSellers,
    path: "/featured-sellers",
  },
  SeeAllFeaturedCars: {
    id: "11",
    element: SeeAllFeaturedCars,
    path: "/featured-cars",
  },
  SeeAllFeaturedBikes: {
    id: "12",
    element: SeeAllFeaturedBikes,
    path: "/featured-bikes",
  },
  BuyerRequestsFeed: {
    id: "13",
    element: BuyerRequestsFeed,
    path: "/feed",
  },
  PrivacyPolicy: {
    id: "14",
    element: PrivacyPolicy,
    path: "/privacy-policy",
  },
  BargainsListings: {
    id: "15",
    element: BargainsListings,
    path: "bargains-listings",
  },
  RegisterShowroom: {
    id: "16",
    element: RegisterShowroom,
    path: "/register-showroom",
  },
  AddCarToShowroom: {
    id: "17",
    element: AddCarToShowroom,
    path: "/add-car-to-showroom/:showroomId",
  },
  ShowroomProfile: {
    id: "19",
    element: ShowroomProfile,
    path: "/showroom-profile/:showroomId",
  },
  NotFound404: { id: "18", element: NotFound404, path: "/*" },
};

function AppContent() {
  const { currentUser } = useAuth();
  const [showroomId, setShowroomId] = useState(null);
  const [hasShowroom, setHasShowroom] = useState(false);
  const [showroomName, setShowroomName] = useState("");

  useEffect(() => {
    const checkUserShowroom = async () => {
      if (currentUser) {
        try {
          const sellersRef = collection(db, "Feature-Seller");
          const q = query(sellersRef, where("userId", "==", currentUser.uid));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const sellerDoc = querySnapshot.docs[0];
            const sellerData = sellerDoc.data();
            setShowroomId(sellerDoc.id);
            setHasShowroom(true);
            setShowroomName(sellerData.sellerName || "Your Showroom");
          } else {
            setHasShowroom(false);
            setShowroomId(null);
          }
        } catch (error) {
          console.error("Error checking showroom:", error);
          setHasShowroom(false);
        }
      } else {
        setHasShowroom(false);
        setShowroomId(null);
      }
    };

    checkUserShowroom();
  }, [currentUser]);

  return (
    <Router>
      <ScrollToTop />
      {/* Top Bar - Dynamic Content */}
      <div
        className="bg-opacity-80 md:py-2"
        style={{ backgroundColor: "#0a1f44" }}
      >
        <div className="container mx-auto flex justify-between items-center px-4">
          {hasShowroom && showroomId ? (
            <p className="text-white text-center text-sm md:text-base flex-1">
              <span className="font-semibold">🏪 Welcome to your showroom</span>
              <span className="hidden sm:inline ml-2">-</span>
              <span className="ml-1 md:ml-2">{showroomName}</span>
            </p>
          ) : (
            <p className="text-white text-center text-sm md:text-base flex-1">
              <span className="font-semibold">🚀 Become a Partner</span>
              <span className="hidden sm:inline ml-2">-</span>
              <span className="ml-1 md:ml-2">Register your showroom with us today!</span>
            </p>
          )}
          
          {hasShowroom && showroomId ? (
            <Link
              to={`/showroom-profile/${showroomId}`}
              className="bg-white text-[#0a1f44] hover:bg-blue-50 px-4 py-1.5 rounded-md font-medium text-sm transition-colors duration-200 flex items-center gap-2 whitespace-nowrap"
            >
              <span>Visit Your Showroom</span>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-4 w-4" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          ) : (
            <Link
              to="/register-showroom"
              className="bg-white text-[#0a1f44] hover:bg-blue-50 px-4 py-1.5 rounded-md font-medium text-sm transition-colors duration-200 flex items-center gap-2 whitespace-nowrap"
            >
              <span>Register Now</span>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-4 w-4" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          )}
        </div>
      </div>
      <Header />
      <Routes>
        {Object.entries(ROUTES).map((route) => {
          const [key, value] = route;
          const TopLevelComponent = value.element;
          return (
            <Route
              key={key}
              path={value.path}
              element={<TopLevelComponent />}
            />
          );
        })}

        <Route path="/profile" element={<PrivateRoute route="/sign-in" />}>
          <Route path="/profile" element={<Profile />} />
        </Route>
        <Route
          path="/contact/:carOwnerId"
          element={<PrivateRoute route="/sign-in" />}
        />
      </Routes>
      <Footer />
      <ToastContainer />
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <LoadingProvider>
        <Helmet>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta
            name="description"
            content="NCP Wheels - Your trusted platform for buying, selling, and trading cars. Explore a wide range of vehicles, car deals, offers, financing, and services for new and used cars."
          />
          <meta
            name="keywords"
            content="ncp cars,swat ncp cars, 
  ncp cars for sale in swat, 
  ncp cars for sale in swat pakistan, 
  ncp car for sale in swat, 
  ncp cars for sale in swat olx, 
  ncp cars in swat, 
  ncp cars swat, 
  ncp cars swat chakdara, 
  ncp swat chakdara motors, 
  cars for sale in swat, 
  car for sale in swat, non custom cars, ncp car, non custom paid cars, ncp cars for sale in swat, ncp cars for sale in swat pakistan, ncp car for sale in swat, ncp cars for sale in pakistan, ncp cars in pakistan for sale, ncp car for sale in pakistan, pakistan non custom cars for sale, non custom car for sale in pakistan, non custom cars in pakistan for sale, non custom cars for sale in pakistan, ncp car for sale, ncp cars for sale in swat olx, ncp cars for sale in chaman, ncp cars in swat, non custom paid cars in chaman, chaman border cars, ncp cars swat, swat ncp cars, non custom paid cars chaman, non custom cars in chaman, chaman non custom cars, chaman border non custom cars price, ncp cars in pakistan, cars for sale in swat, ncp cars swat chakdara, ncp swat chakdara motors, car for sale in swat, chaman border cars for sale in pakistan, non custom car price in chaman"
          />
          <meta name="author" content="NCP Wheels" />
          <meta name="robots" content="index, follow" />
          <title>NCP Wheels | Buy, Sell & Trade Cars</title>

          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.ncpwheels.com/" />
          <meta
            property="og:title"
            content="NCP Wheels | Buy, Sell & Trade Cars"
          />
          <meta
            property="og:description"
            content="NCP Wheels - Your trusted platform for buying, selling, and trading cars. Explore a wide range of vehicles, deals, financing, and services."
          />
          <meta
            property="og:image"
            content="https://www.ncpwheels.com/og-image.jpg"
          />
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content="https://www.ncpwheels.com/" />
          <meta
            property="twitter:title"
            content="NCP Wheels | Buy, Sell & Trade Cars"
          />
          <meta
            property="twitter:description"
            content="NCP Wheels - Your trusted platform for buying, selling, and trading cars. Explore a wide range of vehicles, deals, financing, and services."
          />
          <meta
            property="twitter:image"
            content="https://www.ncpwheels.com/twitter-image.jpg"
          />
        </Helmet>
        <UserProvider>
          <AppContent />
          <a
            href="https://wa.me/923487172565"
            target="_blank"
            rel="noopener noreferrer"
            className="fixed bottom-5 right-5 z-50 bg-green-500 hover:bg-green-600 p-3 rounded-full shadow-lg"
            title="Chat with us on WhatsApp"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="white"
              viewBox="0 0 24 24"
              width="30"
              height="30"
            >
              <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.945C.157 5.3 5.52 0 12.057 0c3.187 0 6.167 1.24 8.413 3.488a11.823 11.823 0 0 1 3.488 8.41c-.003 6.538-5.367 11.902-11.903 11.902a11.9 11.9 0 0 1-5.938-1.59L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.402 1.593 5.448 0 9.885-4.433 9.888-9.884.002-5.462-4.418-9.887-9.883-9.89-5.45 0-9.884 4.435-9.884 9.89 0 2.126.615 3.72 1.657 5.405l-.999 3.648 3.819-.762zM17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.148-.67.15-.197.297-.768.966-.941 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.52-.075-.148-.669-1.612-.916-2.21-.242-.579-.487-.5-.67-.51-.173-.007-.372-.009-.571-.009-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
            </svg>
          </a>
        </UserProvider>
      </LoadingProvider>
    </AuthProvider>
  );
}

export default App;